<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { useUserStore } from '@/stores/user'
import { useRouter } from 'vue-router'
import { API, HOST } from '@/utils/http'
import { toast } from '@/components/ui/toast'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '@/components/ui/dialog'
import { Plus, Edit, Trash2 } from 'lucide-vue-next'
import AdminNavigation from '@/components/admin/AdminNavigation.vue'
import DialogConfirm from '@/components/dialogs/DialogConfirm.vue'
import type { Skill } from '@/types/Skill'

const router = useRouter()
const userStore = useUserStore()
const member = computed(() => userStore.member)

const skills = ref<Skill[]>([])
const loading = ref(false)

const dialogOpen = ref(false)
const editing = ref<Skill | null>(null)
const form = ref({ name: '', description: '', prompt: '', skill_type: 'daily_review', default_lookback_days: 1 })

const confirmDeleteDialog = ref<InstanceType<typeof DialogConfirm> | null>(null)
const pendingDelete = ref<Skill | null>(null)

const fetchSkills = async () => {
  loading.value = true
  try {
    const response = await API({ method: 'GET', url: `${HOST}/admin/skills` })
    skills.value = response.data
  } catch (error) {
    toast({ title: 'Error', description: 'Failed to fetch skills', variant: 'destructive' })
  } finally {
    loading.value = false
  }
}

const openCreate = () => {
  editing.value = null
  form.value = { name: '', description: '', prompt: '', skill_type: 'daily_review', default_lookback_days: 1 }
  dialogOpen.value = true
}

const openEdit = (skill: Skill) => {
  editing.value = skill
  form.value = {
    name: skill.name,
    description: skill.description || '',
    prompt: skill.prompt || '',
    skill_type: skill.skill_type || 'daily_review',
    default_lookback_days: skill.default_lookback_days ?? 1
  }
  dialogOpen.value = true
}

const saveSkill = async () => {
  if (!form.value.name.trim()) {
    toast({ title: 'Name is required', variant: 'destructive' })
    return
  }
  if (!form.value.prompt.trim()) {
    toast({ title: 'Prompt is required', variant: 'destructive' })
    return
  }
  try {
    const data = {
      name: form.value.name,
      description: form.value.description || null,
      prompt: form.value.prompt,
      skill_type: form.value.skill_type || 'daily_review',
      default_lookback_days: Number(form.value.default_lookback_days) || 1
    }
    if (editing.value) {
      await API({ method: 'PATCH', url: `${HOST}/admin/skills/${editing.value.id}`, data })
      toast({ title: 'Skill updated' })
    } else {
      await API({ method: 'POST', url: `${HOST}/admin/skills`, data })
      toast({ title: 'Skill created' })
    }
    dialogOpen.value = false
    await fetchSkills()
  } catch (error: any) {
    const message = error.response?.data?.detail || 'Failed to save skill'
    toast({ title: message, variant: 'destructive' })
  }
}

const promptDelete = (skill: Skill) => {
  pendingDelete.value = skill
  confirmDeleteDialog.value?.open()
}

const deleteSkill = async () => {
  if (!pendingDelete.value) return
  try {
    await API({ method: 'DELETE', url: `${HOST}/admin/skills/${pendingDelete.value.id}` })
    toast({ title: 'Skill deleted' })
    await fetchSkills()
  } catch (error: any) {
    const message = error.response?.data?.detail || 'Failed to delete skill'
    toast({ title: message, variant: 'destructive' })
  } finally {
    pendingDelete.value = null
  }
}

onMounted(() => {
  if (!member.value?.is_super_user) {
    router.push({ name: 'Dashboard' })
    return
  }
  fetchSkills()
})
</script>

<template>
  <div v-if="member && member.is_super_user" class="h-full flex flex-col">
    <AdminNavigation />
    <div class="p-6 space-y-4 flex-1 overflow-auto">
      <div class="flex items-center justify-between">
        <h1 class="text-2xl font-bold">Skills</h1>
        <Button variant="default" size="sm" @click="openCreate">
          <Plus class="mr-2 h-4 w-4" />
          New Skill
        </Button>
      </div>

      <div class="text-sm text-muted-foreground">
        Showing {{ skills.length }} skill{{ skills.length !== 1 ? 's' : '' }}
      </div>

      <div v-if="loading" class="p-4 space-y-3">
        <div v-for="i in 5" :key="i" class="h-10 bg-muted rounded animate-pulse"></div>
      </div>

      <div v-else class="border rounded-md">
        <table class="w-full">
          <thead class="border-b bg-muted/50">
            <tr>
              <th class="h-12 px-4 text-left align-middle font-medium text-muted-foreground">Name</th>
              <th class="h-12 px-4 text-left align-middle font-medium text-muted-foreground">Key</th>
              <th class="h-12 px-4 text-left align-middle font-medium text-muted-foreground">Type</th>
              <th class="h-12 px-4 text-left align-middle font-medium text-muted-foreground">Prompt</th>
              <th class="h-12 px-4 text-left align-middle font-medium text-muted-foreground">Lookback</th>
              <th class="h-12 px-4 text-left align-middle font-medium text-muted-foreground">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="skill in skills" :key="skill.id" class="border-b hover:bg-muted/50">
              <td class="p-4 align-middle font-medium">{{ skill.name }}</td>
              <td class="p-4 align-middle">
                <code class="text-xs text-muted-foreground">{{ skill.key }}</code>
              </td>
              <td class="p-4 align-middle text-sm text-muted-foreground">{{ skill.skill_type }}</td>
              <td class="p-4 align-middle max-w-md">
                <div class="text-sm text-muted-foreground truncate">
                  {{ skill.prompt || '—' }}
                </div>
              </td>
              <td class="p-4 align-middle text-sm text-muted-foreground">{{ skill.default_lookback_days }}d</td>
              <td class="p-4 align-middle">
                <div class="flex items-center gap-1">
                  <Button variant="ghost" size="icon" class="h-8 w-8" @click="openEdit(skill)" title="Edit skill">
                    <Edit class="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" class="h-8 w-8" @click="promptDelete(skill)" title="Delete skill">
                    <Trash2 class="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              </td>
            </tr>
            <tr v-if="skills.length === 0">
              <td colspan="6" class="text-center py-8 text-muted-foreground text-sm">No skills yet.</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <Dialog v-model:open="dialogOpen">
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{{ editing ? 'Edit Skill' : 'New Skill' }}</DialogTitle>
        </DialogHeader>
        <div class="space-y-4">
          <div class="space-y-1">
            <Label>Name</Label>
            <Input v-model="form.name" placeholder="e.g. Daily Review" />
          </div>
          <div class="space-y-1">
            <Label>Description</Label>
            <Input v-model="form.description" placeholder="Short description" />
          </div>
          <div class="space-y-1">
            <Label>Prompt</Label>
            <Textarea v-model="form.prompt" rows="5" placeholder="Instruction the model follows when generating this skill's output" />
          </div>
          <div class="grid grid-cols-2 gap-3">
            <div class="space-y-1">
              <Label>Type</Label>
              <Input v-model="form.skill_type" placeholder="daily_review" />
            </div>
            <div class="space-y-1">
              <Label>Default lookback (days)</Label>
              <Input v-model="form.default_lookback_days" type="number" min="1" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" @click="dialogOpen = false">Cancel</Button>
          <Button @click="saveSkill">{{ editing ? 'Save' : 'Create' }}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <DialogConfirm
      ref="confirmDeleteDialog"
      title="Delete skill?"
      description="This soft-deletes the skill. Projects with it activated keep their config but the skill stops running. This action cannot be undone."
      @confirm="deleteSkill"
    />
  </div>
</template>
